import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { CdkScrollable } from '@angular/cdk/scrolling';

import {
    afterNextRender,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    HostListener,
    inject,
    Injector,
    signal,
    untracked,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RaLoadingComponent } from '@shared/components';
import { RaBadgeComponent } from 'src/app/shared/components/ra-badge';
import { RaEmptyComponent } from 'src/app/shared/components/ra-empty/ra-empty.component';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaModalEditTextComponent } from 'src/app/shared/components/ra-modal-edit-text/ra-modal-edit-text.component';
import { RaTooltipDirective } from 'src/app/shared/directives/ra-tooltip/ra-tooltip.directive';
import { EditDataService, ItemFocusRequest } from '../../services/_edit-data.service';
import { EditItemsService } from '../../services/edit-items.service';
import { EditService } from '../../services/edit-service';
import { FieldRemovalService } from '../../services/field-removal.service';
import { ImageColumnsService } from '../../services/image-columns.service';
import { MenuOptionsService } from '../../services/menu-options.service';
import { TextEditingService } from '../../services/text-editing.service';
import { DocumentItemPartial } from 'src/app/domain/entities/partials/documents/document-item.partial';
import { FolderCardComponent } from '../cards/folder-card/folder-card.component';
import { GroupImageCardComponent } from '../cards/group-image-card/group-image-card.component';
import { ImageCardComponent } from '../cards/image-card/image-card.component';
import { CalloutCardComponent } from '../cards/callout-card/callout-card.component';
import { StatsCardComponent } from '../cards/stats-card/stats-card.component';
import { TableCardComponent } from '../cards/table-card/table-card.component';
import { SemaphoreCardComponent } from '../cards/semaphore-card/semaphore-card.component';
import { ChartCardComponent } from '../cards/chart-card/chart-card.component';
import { PageBreakCardComponent } from '../cards/page-break-card/page-break-card.component';
import { TextCardComponent } from '../cards/text-card/text-card.component';
import { VideoCardComponent } from '../cards/video-card/video-card.component';
import { ModalImageColumnsComponent } from '../modals/modal-image-columns/modal-image-columns.component';
import { ModalChartComponent } from '../modals/modal-chart/modal-chart.component';
import { ChartWizardService } from '../../services/chart-wizard.service';
import { ModalListFavoriteFieldsComponent } from '../modals/modal-list-favorite-fields/modal-list-favorite-fields.component';
import { ModalListSectionComponent } from '../modals/modal-list-section/modal-list-section.component';
import { ItemCardSkeletonComponent } from './item-card-skeleton/item-card-skeleton.component';
import { MenuOptionsComponent } from './menu-options/menu-options.component';
import { flyArrowToInsert } from './insert-field-fab/arrow-to-insert-flight';
import { InsertFieldFabComponent } from './insert-field-fab/insert-field-fab.component';

@Component({
    selector: 'section-items',
    templateUrl: 'section-items.component.html',
    styleUrl: 'section-items.component.scss',
    // O host é o contêiner que rola (overflow: auto no :host); registrá-lo como
    // CdkScrollable permite ao cdkDropList auto-scrollar durante o drag de um
    // card perto das bordas — sem isto o CDK só rolaria o próprio drop list.
    hostDirectives: [CdkScrollable],
    imports: [
        DragDropModule,
        TextCardComponent,
        ImageCardComponent,
        VideoCardComponent,
        RaTooltipDirective,
        MenuOptionsComponent,
        InsertFieldFabComponent,
        GroupImageCardComponent,
        FolderCardComponent,
        PageBreakCardComponent,
        CalloutCardComponent,
        StatsCardComponent,
        TableCardComponent,
        SemaphoreCardComponent,
        ChartCardComponent,
        ItemCardSkeletonComponent,
        RaEmptyComponent,
        RaLoadingComponent,
        RaIconComponent,
        ModalListSectionComponent,
        ModalListFavoriteFieldsComponent,
        ModalImageColumnsComponent,
        ModalChartComponent,
        RaBadgeComponent,
        RaModalEditTextComponent,
        TranslatePipe,
    ],
})
export class SectionItemsComponent {
    /** Assistente do gráfico (aberto pelo menu "Inserir campo" ou pelo card do gráfico). */
    public chartWizard = inject(ChartWizardService);
    public showModalListSession = signal(false);
    public showModalListFavoriteFields = signal(false);
    public showScrollToBottom = signal(false);
    /**
     * A cascata de entrada dos cards (30ms entre um e outro) vale só quando a seção abre.
     * Depois, um card inserido no meio esperava a vez dele e ficava invisível por até 330ms.
     */
    public staggerEnter = signal(true);
    private _staggerTimer: ReturnType<typeof setTimeout> | undefined;
    public showRenameFolder = signal(false);

    /** `true` quando o documento está em modo checklist. */
    public isChecklistMode = computed(
        () => this.editDataService.document()?.config?.checklistMode ?? false
    );

    /** Itens do nível atual: no nível da seção, itens sem pasta (+ cards de pasta);
     *  dentro de uma pasta, só os filhos dela. */
    public displayedItems = computed(() => {
        const items = this.editDataService.currentSession()?.items ?? [];
        const folderId = this.editDataService.currentFolderId();
        return items.filter(item => (item.parentFolderId ?? null) === folderId);
    });

    /** Pasta aberta no momento (`null` = nível da seção). */
    public currentFolder = computed(() => {
        const folderId = this.editDataService.currentFolderId();
        if (!folderId) return null;
        return (
            (this.editDataService.currentSession()?.items ?? []).find(
                item => item._id === folderId
            ) ?? null
        );
    });

    /** Quantidade de skeletons exibidos enquanto a seção carrega. */
    public itemSkeletons = Array.from({ length: 3 });

    private readonly _host = inject(ElementRef<HTMLElement>);
    private readonly _injector = inject(Injector);

    /** Evita scroll no carregamento / troca de seção; só quando a quantidade de itens aumenta na mesma seção. */
    private _itemsScrollSessionId: string | undefined;
    private _itemsScrollLastCount = 0;
    private _knownItemIds = new Set<string>();
    private _lastEditingFieldId: string | null = null;

    /** Garante o scroll automático ao último item apenas na primeira carga da tela. */
    private _didInitialScroll = false;

    // * [Injects]
    public editService = inject(EditService);
    public editDataService = inject(EditDataService);

    /** Item que ABRE uma folha do PDF → número da folha (traço de quebra acima do card). */
    public pdfPageStartByItemId = this.editDataService.pdfPageStartByItemId;

    /** Item cortado pela virada de folha → página onde ele continua (aviso no rodapé do card). */
    public pdfContinuesByItemId = this.editDataService.pdfContinuesByItemId;
    public pdfRowBreaksByItemId = this.editDataService.pdfRowBreaksByItemId;
    public menuOptionsService = inject(MenuOptionsService);
    public textEditing = inject(TextEditingService);
    public imageColumnsService = inject(ImageColumnsService);
    private _editItemsService = inject(EditItemsService);
    private _fieldRemovalService = inject(FieldRemovalService);

    constructor() {
        // O modal de colunas é renderizado aqui: saindo da tela com a pergunta
        // aberta (troca para a aba de orçamento, por exemplo), o fluxo de upload
        // ficaria esperando para sempre uma resposta que ninguém mais pode dar.
        inject(DestroyRef).onDestroy(() => this.imageColumnsService.cancel());

        effect(() => {
            const session = this.editDataService.currentSession();
            const sessionId = session?._id;
            const len = session?.items?.length ?? 0;

            if (sessionId !== this._itemsScrollSessionId) {
                // Ao trocar de seção, encerra qualquer edição em andamento: o campo em
                // edição pertence à seção anterior.
                if (this._itemsScrollSessionId !== undefined) {
                    untracked(() => this.textEditing.clear());
                }

                // Trocou de seção → sai de qualquer pasta aberta (volta ao nível da seção).
                untracked(() => this.editDataService.setCurrentFolderId(null));

                this._itemsScrollSessionId = sessionId;
                this._itemsScrollLastCount = len;

                untracked(() => {
                    this.staggerEnter.set(true);
                    clearTimeout(this._staggerTimer);
                    this._staggerTimer = setTimeout(() => this.staggerEnter.set(false), 700);
                });

                // Na primeira carga da tela, rola direto para o último item da seção.
                if (!this._didInitialScroll && sessionId && len > 0) {
                    this._didInitialScroll = true;
                    this.scheduleInitialScrollToEnd();
                } else {
                    this.scheduleUpdateScrollToBottomVisibility();
                }
                return;
            }

            if (len > this._itemsScrollLastCount) {
                this._itemsScrollLastCount = len;
                // Só desce até o fim quando o item novo É o último e a pessoa já está perto do
                // fim. Antes qualquer item novo (inserir no meio, duplicar, desfazer, ou um
                // campo criado por OUTRA pessoa) arrastava a folha até o fim no meio do que ela
                // estava fazendo. Campo de texto novo vem para a tela pelo pedido de edição.
                const items = session?.items ?? [];
                const lastIsNew = !!items.length && !this._knownItemIds.has(items[items.length - 1]._id);
                if (lastIsNew && untracked(() => this._isNearBottom())) {
                    this.scheduleScrollItemsListToEnd();
                } else {
                    this.scheduleUpdateScrollToBottomVisibility();
                }
            } else {
                this._itemsScrollLastCount = len;
                this.scheduleUpdateScrollToBottomVisibility();
            }
        });

        // Ids já vistos na seção aberta: é o que diz se o último item acabou de chegar.
        effect(() => {
            const items = this.editDataService.currentSession()?.items ?? [];
            // Depois do efeito acima (efeitos rodam na ordem): a comparação vê o estado anterior.
            queueMicrotask(() => (this._knownItemIds = new Set(items.map(item => item._id))));
        });

        // Campo que entrou em edição (campo novo, "Editar" na prévia) vem para a tela.
        effect(() => {
            const editingFieldId = this.textEditing.editingFieldId();
            if (editingFieldId && editingFieldId !== this._lastEditingFieldId) {
                this.scheduleScrollEditingFieldIntoView(editingFieldId);
            }

            this._lastEditingFieldId = editingFieldId;
        });

        /*
         * A prévia pediu para trazer um card para a tela (menu de ações sobre o item).
         *
         * O pedido chega junto com a troca de seção — e às vezes com a abertura de uma pasta
         * de checklist —, então o card pode nem existir ainda quando ele chega. Por isso a
         * procura se repete por alguns frames antes de desistir.
         */
        effect(() => {
            const request = this.editDataService.focusRequest();
            if (!request) return;

            untracked(() => this.scheduleFocusField(request));
        });

        inject(DestroyRef).onDestroy(() => {
            clearTimeout(this._focusFlashTimer);
            clearTimeout(this._staggerTimer);
        });
    }

    /** Mesma guarda do clique no card de texto (ver text-card: isBlocked). */
    private isEditingBlocked(): boolean {
        return (
            this.editService.loading() ||
            this.editDataService.currentSessionIsDisabled() ||
            this.editDataService.documentIsBlocked()
        );
    }

    /** Item sob o mouse na prévia: o card dele acende (ver .is-preview-hover). */
    public previewHoverItemId = this.editDataService.previewHoverItemId;

    /** Tentativas de achar o card pedido pela prévia (~1s a 60fps). */
    private static readonly FOCUS_MAX_ATTEMPTS = 60;

    /** Quanto tempo o card fica destacado depois de a prévia trazer o usuário até ele. */
    private static readonly FOCUS_FLASH_MS = 1600;

    private _focusFlashTimer?: ReturnType<typeof setTimeout>;

    private scheduleFocusField(request: ItemFocusRequest): void {
        afterNextRender(() => this.tryFocusField(request, 0), { injector: this._injector });
    }

    private tryFocusField(request: ItemFocusRequest, attempt: number): void {
        const fieldId = request.itemId;
        const host = this._host.nativeElement as HTMLElement;
        const target = Array.from(host.querySelectorAll<HTMLElement>('[data-field-id]')).find(
            item => item.dataset['fieldId'] === fieldId
        );

        if (!target) {
            if (attempt < SectionItemsComponent.FOCUS_MAX_ATTEMPTS) {
                requestAnimationFrame(() => this.tryFocusField(request, attempt + 1));
            } else {
                this.editDataService.clearItemFocus();
            }
            return;
        }

        this.editDataService.clearItemFocus();

        // "Editar" na prévia: foca o editor inline do campo, como o clique no card. Aqui, e
        // não no painel, porque a troca de seção que trouxe o pedido encerra a edição;
        // chegando aqui o card já existe, logo a limpeza já passou. A mesma guarda do card:
        // seção travada por outro usuário ou documento bloqueado não editam.
        if (request.edit && !this.isEditingBlocked()) {
            const field = this.displayedItems().find(item => item._id === fieldId);
            if (field) this.textEditing.requestEdit(field._id);
        }

        this.scrollEditingFieldIntoView(fieldId);

        // Destaque pela classe no elemento, e não por signal + binding: é um piscar de olho,
        // não estado do card, e o @for mantém o mesmo nó (track pelo _id) enquanto dura.
        clearTimeout(this._focusFlashTimer);
        host.querySelectorAll('.drag-item.is-focused').forEach(el =>
            el.classList.remove('is-focused')
        );
        target.classList.add('is-focused');
        this._focusFlashTimer = setTimeout(
            () => target.classList.remove('is-focused'),
            SectionItemsComponent.FOCUS_FLASH_MS
        );
    }

    @HostListener('scroll')
    public onScroll(): void {
        this.updateScrollToBottomVisibility();
    }

    private scheduleScrollItemsListToEnd(): void {
        afterNextRender(
            () => {
                requestAnimationFrame(() => this.scrollItemsListHostToEnd());
            },
            { injector: this._injector }
        );
    }

    /**
     * Scroll inicial até o último item. Reforça algumas vezes porque os cards
     * (e mídias) ainda ajustam a altura após o primeiro render, então o
     * scrollHeight só fica correto depois que tudo estabiliza.
     */
    private scheduleInitialScrollToEnd(): void {
        afterNextRender(
            () => {
                for (const delay of [0, 120, 300, 600]) {
                    setTimeout(
                        () =>
                            // Sem animação: são até 4 ajustes enquanto as mídias carregam, e
                            // suaves eles apareciam como a folha descendo aos trancos.
                            requestAnimationFrame(() => this.scrollItemsListHostToEnd('auto')),
                        delay
                    );
                }
            },
            { injector: this._injector }
        );
    }

    private scheduleUpdateScrollToBottomVisibility(): void {
        afterNextRender(
            () => {
                requestAnimationFrame(() => this.updateScrollToBottomVisibility());
            },
            { injector: this._injector }
        );
    }

    private scheduleScrollEditingFieldIntoView(fieldId: string): void {
        afterNextRender(
            () => {
                requestAnimationFrame(() => this.scrollEditingFieldIntoView(fieldId));
            },
            { injector: this._injector }
        );
    }

    private scrollItemsListHostToEnd(behavior: ScrollBehavior = 'smooth'): void {
        const el = this._host.nativeElement;
        if (!el || el.scrollHeight <= el.clientHeight) {
            this.showScrollToBottom.set(false);
            return;
        }

        el.scrollTo({ top: el.scrollHeight, behavior });
        this.showScrollToBottom.set(false);
    }

    private scrollEditingFieldIntoView(fieldId: string): void {
        const el = this._host.nativeElement as HTMLElement;
        const fieldElements = Array.from(el.querySelectorAll('[data-field-id]')) as HTMLElement[];
        const fieldElement = fieldElements.find(item => item.dataset['fieldId'] === fieldId);
        if (!fieldElement) return;

        const hostRect = el.getBoundingClientRect();
        const fieldRect = fieldElement.getBoundingClientRect();
        const spacing = 16;
        const safeTop = hostRect.top + spacing;
        const safeBottom = hostRect.bottom - spacing;
        const safeHeight = safeBottom - safeTop;

        // Já está à vista: não rola. Clicar num texto no meio da tela fazia a folha andar e o
        // texto sair de baixo do cursor. Campo novo pedido por código (focusRequest) precisa
        // estar inteiro; o clique do usuário, só não pode estar fora da tela.
        const requested = this.textEditing.focusRequest()?.fieldId === fieldId;
        const fullyVisible = fieldRect.top >= safeTop && fieldRect.bottom <= safeBottom;
        const partlyVisible = fieldRect.bottom > safeTop && fieldRect.top < safeBottom;
        if (fullyVisible || (!requested && partlyVisible)) return;
        const targetTop =
            fieldRect.height > safeHeight
                ? el.scrollTop + fieldRect.top - safeTop
                : el.scrollTop + fieldRect.bottom - safeBottom;
        const maxTop = el.scrollHeight - el.clientHeight;

        el.scrollTo({
            top: Math.max(0, Math.min(targetTop, maxTop)),
            behavior: 'smooth',
        });
    }

    /** A rolagem está a menos de uma tela do fim. */
    private _isNearBottom(): boolean {
        const el = this._host.nativeElement as HTMLElement;
        return el.scrollHeight - el.scrollTop - el.clientHeight < el.clientHeight;
    }

    /** A seta vira o "Inserir campo": rola até o fim levando a bolinha até o botão. */
    public scrollToBottom(): void {
        const host = this._host.nativeElement as HTMLElement;
        void flyArrowToInsert(
            host,
            host.querySelector<HTMLElement>('.button-scroll-to-bottom'),
            host.querySelector<HTMLElement>('insert-field-fab')
        ).then(() => this.updateScrollToBottomVisibility());
    }

    /** A linha de inserção deste item está com o menu aberto (fica visível sem hover). */
    public insertOpenAfter(fieldId: string): boolean {
        return (
            !this.menuOptionsService.isMenuOptionsHidden() &&
            this.menuOptionsService.belowFieldId() === fieldId
        );
    }

    /**
     * "+ Inserir campo" entre dois itens: o menu abre com "abaixo deste campo", ancorado no
     * CARD de cima (não na pílula, que fica no meio da folha): na margem cinza ao lado, com
     * a base na divisa dos dois cards.
     */
    /** A pílula de cima do primeiro item está com o menu aberto. */
    public insertOpenBefore(fieldId: string): boolean {
        return (
            !this.menuOptionsService.isMenuOptionsHidden() &&
            this.menuOptionsService.aboveFieldId() === fieldId
        );
    }

    /** Campo novo acima do primeiro item (`aboveFieldId`: entra antes dele). */
    public toggleInsertBefore(fieldId: string, event: Event): void {
        if (this.insertOpenBefore(fieldId)) {
            this.menuOptionsService.hideMenuOptions();
            return;
        }

        const host = this._host.nativeElement as HTMLElement;
        const card =
            Array.from(host.querySelectorAll<HTMLElement>('[data-field-id]')).find(
                element => element.dataset['fieldId'] === fieldId
            ) ?? (event.currentTarget as HTMLElement);
        this.menuOptionsService.showMenuOptions(fieldId, undefined, card);
    }

    public toggleInsertAfter(fieldId: string, event: Event): void {
        if (this.insertOpenAfter(fieldId)) {
            this.menuOptionsService.hideMenuOptions();
            return;
        }

        const host = this._host.nativeElement as HTMLElement;
        const card =
            Array.from(host.querySelectorAll<HTMLElement>('[data-field-id]')).find(
                element => element.dataset['fieldId'] === fieldId
            ) ?? (event.currentTarget as HTMLElement);
        this.menuOptionsService.showMenuOptions(undefined, fieldId, card);
    }

    /**
     * A seta aparece só quando o FIM da folha está fora da tela: o botão "Inserir campo" (ou,
     * sem ele, o último campo). Não conta a folga de rolagem que fica abaixo do botão; com
     * tudo à vista, rolar até o fim não mostraria nada novo.
     */
    private updateScrollToBottomVisibility(): void {
        const el = this._host.nativeElement as HTMLElement;
        const fab = el.querySelector<HTMLElement>('insert-field-fab');
        const lastField = Array.from(el.querySelectorAll<HTMLElement>('[data-field-id]')).pop();

        // O invólucro do botão, e não o botão: o botão fica deslocado até a animação de
        // entrada rodar. O topo do invólucro é o topo do botão (a folga fica embaixo).
        let endBottom: number | null = null;
        if (fab) endBottom = fab.getBoundingClientRect().top + Math.min(fab.offsetHeight, 48);
        else if (lastField) endBottom = lastField.getBoundingClientRect().bottom;

        if (endBottom === null) {
            const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            this.showScrollToBottom.set(distanceFromBottom > 8);
            return;
        }

        this.showScrollToBottom.set(endBottom - el.getBoundingClientRect().bottom > 8);
    }

    /**
     * Handler para quando um item é arrastado e solto
     * @param event Evento de drag and drop
     */
    public drop(event: CdkDragDrop<any[]>): void {
        if (event.previousIndex === event.currentIndex) return;

        // A lista exibida é FILTRADA por pasta (displayedItems), mas o reorder
        // opera no array COMPLETO de itens da seção (com os filhos das pastas).
        // Mapeamos os índices da lista exibida → índices reais por id.
        const displayed = this.displayedItems();
        const full = this.editDataService.currentSession()?.items ?? [];

        const moved = displayed[event.previousIndex];
        if (!moved?._id) return;
        const sourceIndex = full.findIndex(item => item._id === moved._id);
        if (sourceIndex < 0) return;

        // Ordem exibida já com o item movido na posição de destino.
        const newDisplayed = [...displayed];
        newDisplayed.splice(event.previousIndex, 1);
        newDisplayed.splice(event.currentIndex, 0, moved);

        // Item que fica logo após o movido define onde inserir no array completo.
        // (reorderItems remove o source e insere no targetIndex — mesma semântica.)
        const after = newDisplayed[event.currentIndex + 1];
        let targetIndex: number;
        if (after?._id) {
            const afterFull = full.findIndex(item => item._id === after._id);
            if (afterFull < 0) return;
            targetIndex = afterFull > sourceIndex ? afterFull - 1 : afterFull;
        } else {
            targetIndex = full.length - 1;
        }

        if (targetIndex === sourceIndex) return;

        // Reorder é por ÍNDICE: uma remoção pendente de desfazer deixaria os
        // índices locais e os do servidor divergentes — confirma antes.
        this._fieldRemovalService.flush().subscribe({
            next: () => this.editDataService.reorderItems(sourceIndex, targetIndex),
        });
    }

    /**
     * Remove itens selecionados
     */
    public removerSelectedItems(): void {
        const itemIds = this.editDataService.selectedItems().map(item => item._id);
        this._editItemsService.removeItem(itemIds);

        // Limpar seleções
        this.editDataService.cleanSelectedItems();
    }

    /** Entra numa pasta de checklist (navegação in-place via currentFolderId). */
    public openFolder(folder: DocumentItemPartial): void {
        this.editDataService.setCurrentFolderId(folder._id);
    }

    /** Volta ao nível da seção. */
    public exitFolder(): void {
        this.editDataService.setCurrentFolderId(null);
    }

    /** Renomeia a pasta aberta (modal). */
    public saveRenameFolder(name: string): void {
        const sessionId = this.editDataService.getCurrentSessionId();
        const folderId = this.editDataService.currentFolderId();
        if (sessionId && folderId && name?.trim()) {
            this.editService.renameChecklistFolder(sessionId, folderId, name.trim());
        }
        this.showRenameFolder.set(false);
    }

    /** Remove a pasta aberta (o backend remove os campos-filhos em cascata) e volta. */
    public removeFolder(): void {
        const folderId = this.editDataService.currentFolderId();
        if (!folderId) return;
        this._editItemsService.removeItem([folderId]);
        this.exitFolder();
    }

    public closeModalListFavoriteFields(): void {
        this.showModalListFavoriteFields.set(false);
        this.menuOptionsService.hideMenuOptions();
    }
}
